import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { BarChart3, Building2, CalendarCheck, ClipboardCheck, ClipboardList, CreditCard, Flame, LifeBuoy, LogOut, MessageSquare, Settings, Shield, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { UAT_REVIEWER_PATHS } from './reviewerAccess';

const navItems = [
  { to: '/platform', label: 'Dashboard', icon: BarChart3, end: true },
  { to: '/platform/tenants', label: 'Organisations', icon: Building2 },
  { to: '/platform/customers', label: 'Customers', icon: Users },
  { to: '/platform/bookings', label: 'Bookings', icon: CalendarCheck },
  { to: '/platform/messages', label: 'Messages', icon: MessageSquare },
  { to: '/platform/financials', label: 'Financials', icon: CreditCard },
  { to: '/platform/audit', label: 'Audit Log', icon: ClipboardList },
  { to: '/platform/uat-checklist', label: 'UAT Checklist', icon: ClipboardCheck },
  { to: '/platform/smoke-checklist', label: 'Smoke Tests', icon: Flame },
  { to: '/platform/support', label: 'Support', icon: LifeBuoy },
  { to: '/platform/settings', label: 'Settings', icon: Settings },
];

const PlatformLayout = () => {
  const { user, isPlatformAdmin, isUatReviewer, signOut } = useAuth();
  const navigate = useNavigate();
  const visibleNavItems = isPlatformAdmin
    ? navItems
    : isUatReviewer
      ? navItems.filter((item) => UAT_REVIEWER_PATHS.includes(item.to))
      : [];

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  return (
    <div className="min-h-screen bg-background text-foreground md:flex">
      <aside className="border-b border-border bg-card md:flex md:min-h-screen md:w-72 md:flex-col md:border-b-0 md:border-r">
        <div className="border-b border-border p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Shield className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h1 className="font-display text-lg text-foreground">Platform Admin</h1>
              <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <Badge variant="outline" className="mt-4 border-primary/20 bg-primary/10 text-primary">
            {isPlatformAdmin ? 'Founder tools' : 'UAT access'}
          </Badge>
        </div>

        <nav className="flex gap-2 overflow-x-auto p-3 md:flex-1 md:flex-col md:gap-1 md:overflow-visible md:p-4">
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) => cn(
                  'flex shrink-0 items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors',
                  isActive ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
                )}
              >
                <Icon className="h-4 w-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="hidden border-t border-border p-4 md:block">
          {isPlatformAdmin && (
            <Button variant="outline" className="mb-2 w-full justify-start" asChild>
              <Link to="/admin">Tenant admin</Link>
            </Button>
          )}
          <Button variant="ghost" className="w-full justify-start gap-2" onClick={handleSignOut}>
            <LogOut className="h-4 w-4" />
            Sign out
          </Button>
        </div>
      </aside>

      <main className="min-w-0 flex-1">
        <Outlet />
      </main>
    </div>
  );
};

export default PlatformLayout;
